import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, MessageCircle, Phone, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CONTACT_INFO } from '@/data/mockData';
import { generateWhatsAppLink } from '@/utils/whatsappTemplates';

const QuoteThankYou = () => {
  const navigate = useNavigate();
  const [refId, setRefId] = useState('');
  const [mobile, setMobile] = useState('');

  useEffect(() => {
    const ref = sessionStorage.getItem('quoteRef');
    const mob = sessionStorage.getItem('contactMobile');
    
    if (!ref) {
      navigate('/quote');
      return;
    }
    
    setRefId(ref);
    setMobile(mob || '');
    
    // Clean up session storage
    sessionStorage.removeItem('quoteData');
    sessionStorage.removeItem('quoteRef');
    sessionStorage.removeItem('contactMobile');
  }, [navigate]);

  const whatsappLink = generateWhatsAppLink('generic', { refId });

  const nextSteps = [
    { text: 'We confirm availability and review your details', time: 'Within 15 minutes' },
    { text: 'We send you a clear, detailed quote', time: 'Within 30 minutes' },
    { text: 'You book and receive real-time updates + proof-of-delivery', time: 'Once confirmed' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Progress Indicator */}
      <div className="bg-white border-b py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-4 max-w-2xl mx-auto">
            <div className="flex items-center">
              <div className="bg-[#039fff] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="ml-2 text-sm text-[#039fff]">Details</span>
            </div>
            <div className="h-px w-12 bg-[#039fff]"></div>
            <div className="flex items-center">
              <div className="bg-[#039fff] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="ml-2 text-sm text-[#039fff]">Confirm</span>
            </div>
            <div className="h-px w-12 bg-[#039fff]"></div>
            <div className="flex items-center">
              <div className="bg-[#039fff] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">3</div>
              <span className="ml-2 text-sm font-medium text-[#039fff]">Done</span>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            {/* Success Icon */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
                <CheckCircle className="h-16 w-16 text-green-600" />
              </div>
              <h1 className="text-4xl font-bold text-[#001A47] mb-4">Quote Request Received!</h1>
              <p className="text-xl text-gray-600 mb-6">
                We'll get back to you shortly
              </p>
              
              <div className="inline-block bg-[#039fff]/10 px-6 py-3 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Your Reference Number</p>
                <p className="text-2xl font-bold text-[#039fff]">{refId}</p>
              </div>
            </div>

            {/* Primary Actions */}
            <Card className="mb-8">
              <CardContent className="p-8 text-center">
                <h2 className="text-xl font-bold text-[#001A47] mb-6">Continue the Conversation</h2>
                <div className="space-y-4">
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="w-full bg-[#25D366] hover:bg-[#22c55e] text-white py-6 text-lg">
                      <MessageCircle className="mr-3 h-6 w-6" />
                      Continue on WhatsApp
                    </Button>
                  </a>
                  <a href={`tel:${CONTACT_INFO.phone}`}>
                    <Button size="lg" variant="outline" className="w-full border-2 border-[#039fff] text-[#039fff] hover:bg-[#039fff] hover:text-white py-6 text-lg">
                      <Phone className="mr-3 h-6 w-6" />
                      Call Us Now
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* What Happens Next */}
            <Card>
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-[#001A47] mb-6">What Happens Next?</h3>
                <div className="space-y-4">
                  {nextSteps.map((step, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#039fff] text-white rounded-full flex items-center justify-center font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-[#001A47] font-medium">{step.text}</p>
                        <p className="text-sm text-gray-500">{step.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Additional Actions */}
            <div className="mt-8 text-center space-y-4">
              <p className="text-gray-600">
                Need help? Contact us at <a href={`tel:${CONTACT_INFO.phone}`} className="text-[#039fff] hover:underline font-semibold">{CONTACT_INFO.phone}</a>
              </p>
              <a href="/">
                <Button variant="ghost" className="text-gray-600">
                  <ArrowRight className="mr-2 h-4 w-4 rotate-180" />
                  Back to Home
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuoteThankYou;
